import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFetchAthleteByUserIdQuery } from '../slices/athleteApiSlice';
import {
  useDisconnectGarminMutation,
  useGetGarminAuthUrlMutation,
  useGetGarminStatusQuery,
  useLinkGarminFromRecentPushMutation,
} from '../slices/garminApiSlice';
import { useGetImportStatusQuery, useGetStravaAuthUrlMutation, useImportStravaActivitiesMutation } from '../slices/stravaApiSlice';
import { useLazyRefreshUserProfileQuery } from '../slices/usersApiSlice';
import { setCredentials } from '../slices/authSlice';
import { useDeleteEventMutation, useUpdateEventClassificationMutation } from '../slices/eventApiSlice';
import { useActiveLeagueId } from '../utils/activeLeague';

const formatDateTime = (dateValue) => {
  if (!dateValue) return 'Not available';

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'Not available';

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatMiles = (distanceMeters) => {
  if (typeof distanceMeters !== 'number') return '0.0';
  return (distanceMeters / 1609.34).toFixed(1);
};

const getActivityService = (event) => {
  if (event?.importedFromGarmin) return 'Garmin';
  if (event?.importedFromStrava) return 'Strava';
  const labels = {
    manual: 'Manual',
    strava: 'Strava',
    garmin: 'Garmin',
    apple_health: 'Apple Health',
    apple_watch: 'Apple Watch',
    iphone: 'iPhone',
  };
  if (event?.source && labels[event.source]) return labels[event.source];
  return 'Manual';
};

const getActivityDevice = (event) => {
  const garminLabel = [event?.garminManufacturer, event?.garminProduct]
    .filter(Boolean)
    .join(' ')
    .trim();

  return event?.garminDeviceName || garminLabel || 'Not provided';
};

const ActivitiesScreen = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { userInfo } = useSelector((state) => state.auth);
  const stravaConnected = searchParams.get('strava_connected');
  const garminConnected = searchParams.get('garmin_connected');
  const [deletingEventId, setDeletingEventId] = useState(null);
  const activeLeagueId = useActiveLeagueId();
  const [declaration, setDeclaration] = useState(null);

  const { data: athlete, isLoading: isLoadingAthlete, refetch: refetchAthlete } = useFetchAthleteByUserIdQuery(userInfo?._id, {
    skip: !userInfo?._id,
  });
  const { data: garminStatus, refetch: refetchGarmin } = useGetGarminStatusQuery(undefined, {
    skip: !userInfo,
  });
  const { data: importStatus, refetch: refetchImportStatus } = useGetImportStatusQuery(undefined, {
    skip: !userInfo,
  });
  const [getGarminAuthUrl] = useGetGarminAuthUrlMutation();
  const [getStravaAuthUrl] = useGetStravaAuthUrlMutation();
  const [disconnectGarmin, { isLoading: disconnectingGarmin }] = useDisconnectGarminMutation();
  const [linkGarminFromRecentPush, { isLoading: linkingGarminId }] = useLinkGarminFromRecentPushMutation();
  const [refreshUserProfile] = useLazyRefreshUserProfileQuery();
  const [importActivities, { isLoading: importLoading }] = useImportStravaActivitiesMutation();
  const [deleteEvent] = useDeleteEventMutation();
  const [updateEventClassification, { isLoading: isDeclaring }] = useUpdateEventClassificationMutation();

  const openDeclaration = (event) => {
    const classification = event.scoringClassifications?.find((item) => item.league?.toString() === activeLeagueId?.toString());
    setDeclaration({
      eventId: event._id,
      eventName: event.title || event.name || 'Workout',
      variation: classification?.variation || 'standard',
      ruckWeightLbs: classification?.ruckWeightLbs || '',
    });
  };

  const submitDeclaration = async (event) => {
    event.preventDefault();
    try {
      await updateEventClassification({
        id: declaration.eventId,
        leagueId: activeLeagueId,
        variation: declaration.variation,
        ruckWeightLbs: declaration.ruckWeightLbs,
        status: 'pending',
      }).unwrap();
      setDeclaration(null);
      toast.success(declaration.variation === 'standard' ? 'Activity reset to standard scoring.' : 'Variation sent to your league manager for review.');
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update activity variation.');
    }
  };

  useEffect(() => {
    if (stravaConnected !== 'true' || !userInfo) {
      return;
    }

    const clearQueryFlag = () => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('strava_connected');
      const nextUrl = nextParams.toString() ? `${window.location.pathname}?${nextParams.toString()}` : window.location.pathname;
      window.history.replaceState({}, '', nextUrl);
    };

    const refreshProfile = async () => {
      try {
        const refreshedUser = await refreshUserProfile().unwrap();
        dispatch(setCredentials({ userInfo: { ...userInfo, ...refreshedUser } }));
        toast.success('Strava connected successfully.');
        refetchImportStatus?.();
      } catch (error) {
        console.error('Failed to refresh after Strava connection', error);
        toast.error('Strava connected, but the profile did not refresh cleanly.');
      } finally {
        clearQueryFlag();
      }
    };

    void refreshProfile();
  }, [dispatch, refreshUserProfile, refetchImportStatus, searchParams, stravaConnected, userInfo]);

  useEffect(() => {
    if (garminConnected !== 'true') {
      return;
    }

    toast.success('Garmin connected successfully.');
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('garmin_connected');
    const nextUrl = nextParams.toString() ? `${window.location.pathname}?${nextParams.toString()}` : window.location.pathname;
    window.history.replaceState({}, '', nextUrl);
  }, [garminConnected, searchParams]);

  const handleConnectStrava = async () => {
    try {
      const { authUrl } = await getStravaAuthUrl().unwrap();
      window.location.assign(authUrl);
    } catch (error) {
      console.error('Failed to start Strava auth', error);
      toast.error(error?.data?.message || 'Failed to start Strava connection');
    }
  };

  const handleConnectGarmin = async () => {
    try {
      const { authUrl } = await getGarminAuthUrl().unwrap();
      const popup = window.open(authUrl, 'garmin_oauth', 'width=600,height=700');

      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        toast.info('Your browser blocked the Garmin popup. We are opening Garmin in this tab instead.');
        window.location.assign(authUrl);
        return;
      }

      popup.focus?.();
    } catch (error) {
      console.error('Failed to start Garmin auth', error);
      toast.error(error?.data?.message || 'Failed to start Garmin connection');
    }
  };

  const handleImportActivities = async () => {
    try {
      const result = await importActivities().unwrap();

      if (result.imported > 0) {
        toast.success(`Imported ${result.imported} activities.`);
      } else if (result.message) {
        toast.info(result.message);
      } else {
        toast.info('No new eligible Strava activities were available to import.');
      }

      refetchImportStatus?.();
    } catch (error) {
      console.error('Import failed', error);
      toast.error(error?.data?.message || 'Failed to import activities');
    }
  };

  const handleDisconnectGarmin = async () => {
    try {
      await disconnectGarmin({ notify: true }).unwrap();
      await refetchGarmin();
      toast.success('Garmin disconnected successfully.');
    } catch (error) {
      console.error('Garmin disconnect failed', error);
      toast.error(error?.data?.message || 'Failed to disconnect Garmin');
    }
  };

  const handleLinkGarminId = async () => {
    try {
      const result = await linkGarminFromRecentPush().unwrap();
      await refetchGarmin();

      if (result?.linked) {
        toast.success(`Garmin ID linked${result.backfilled ? ` and ${result.backfilled} activities backfilled` : ''}.`);
        return;
      }

      if (result?.reason === 'multiple-candidates') {
        toast.error('Multiple Garmin IDs were detected in recent syncs, so LeagueRunner will not guess. Please contact support.');
        return;
      }

      toast.info(result?.message || 'Garmin ID is still waiting on a recent sync from Garmin.');
    } catch (error) {
      console.error('Garmin manual link failed', error);
      toast.error(error?.data?.message || 'Failed to link Garmin ID');
    }
  };

  const handleDeleteActivity = async (event) => {
    const eventId = event?._id;
    if (!eventId) return;

    const eventName = event.title || event.name || 'this activity';
    if (!window.confirm(`Delete ${eventName}? This removes its miles and points from LeagueRunner.`)) return;

    setDeletingEventId(eventId);
    try {
      await deleteEvent(eventId).unwrap();
      await Promise.all([refetchAthlete?.(), refetchImportStatus?.()]);
      toast.success('Activity deleted.');
    } catch (error) {
      console.error('Activity deletion failed', error);
      toast.error(error?.data?.message || 'Failed to delete activity');
    } finally {
      setDeletingEventId(null);
    }
  };

  const syncedActivities = useMemo(
    () => [...(athlete?.events || [])]
      .sort((left, right) => new Date(right.startTime).getTime() - new Date(left.startTime).getTime())
      .slice(0, 20),
    [athlete?.events]
  );

  if (isLoadingAthlete) {
    return (
      <div className="section-modern">
        <div className="container-modern">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-secondary mb-0">Loading activity connections...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-modern">
      <div className="container-modern">
        <div className="fade-in">
          <div className="hero-shell mb-5">
            <div className="hero-banner">
              <div className="hero-content-panel">
                <div className="hero-kicker">Activities</div>
                <h1 className="hero-title">Keep your workouts flowing into League Runner.</h1>
                <p className="hero-subtitle">
                  Connect Garmin and Strava, import your latest runs, and verify which synced activities are counting toward your league season.
                </p>
                <div className="hero-actions">
                  <Button className="btn btn-primary-modern" onClick={handleImportActivities} disabled={!userInfo?.hasStravaConnection || importLoading}>
                    {importLoading ? 'Importing...' : 'Import Latest Activities'}
                  </Button>
                  <Button className="btn btn-outline-modern" onClick={handleConnectStrava} disabled={Boolean(userInfo?.hasStravaConnection)}>
                    {userInfo?.hasStravaConnection ? 'Strava Connected' : 'Connect Strava'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <Row className="g-4 mb-4">
            <Col lg={6}>
              <Card className="modern-card h-100">
                <div className="modern-card-header">
                  <h2 className="modern-card-title">Connections</h2>
                  <p className="modern-card-subtitle">Authorize the services that feed your fantasy scoring.</p>
                </div>
                <Card.Body className="d-grid gap-3">
                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div className="fw-semibold">Strava</div>
                      <div className="text-secondary small">Pull your latest workouts into League Runner on demand.</div>
                    </div>
                    <Badge bg={userInfo?.hasStravaConnection ? 'success' : 'secondary'}>
                      {userInfo?.hasStravaConnection ? 'Connected' : 'Not connected'}
                    </Badge>
                  </div>
                  {!userInfo?.hasStravaConnection && (
                    <Button className="btn btn-outline-modern" onClick={handleConnectStrava}>
                      Connect Strava
                    </Button>
                  )}

                  <hr className="my-1" />

                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div className="fw-semibold">Garmin</div>
                      <div className="text-secondary small">Sync workouts automatically from your Garmin account.</div>
                    </div>
                    <Badge bg={garminStatus?.connected ? 'success' : 'secondary'}>
                      {garminStatus?.awaitingLink ? 'Needs ID link' : (garminStatus?.connected ? 'Connected' : 'Not connected')}
                    </Badge>
                  </div>
                  {garminStatus?.manualLinkRequired && (
                    <Alert variant="warning" className="mb-0 py-2">
                      Garmin is connected, but imports are paused until you explicitly link the Garmin ID from a recent sync.
                    </Alert>
                  )}
                  {!garminStatus?.connected ? (
                    <Button className="btn btn-outline-modern" onClick={handleConnectGarmin}>
                      Connect Garmin
                    </Button>
                  ) : (
                    <div className="d-grid gap-2">
                      {garminStatus?.manualLinkRequired && (
                        <Button variant="warning" onClick={handleLinkGarminId} disabled={linkingGarminId}>
                          {linkingGarminId ? 'Linking...' : 'Link Garmin ID'}
                        </Button>
                      )}
                      <Button variant="outline-danger" onClick={handleDisconnectGarmin} disabled={disconnectingGarmin}>
                        {disconnectingGarmin ? 'Disconnecting...' : 'Disconnect Garmin'}
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6}>
              <Card className="modern-card h-100">
                <div className="modern-card-header">
                  <h2 className="modern-card-title">Sync Summary</h2>
                  <p className="modern-card-subtitle">A quick check on what has already landed in your account.</p>
                </div>
                <Card.Body>
                  <div className="grid-modern grid-cols-2 gap-3">
                    <div>
                      <div className="text-secondary small mb-1">Strava imports</div>
                      <div className="fs-3 fw-semibold text-primary">{importStatus?.totalImportedActivities ?? 0}</div>
                    </div>
                    <div>
                      <div className="text-secondary small mb-1">Last import</div>
                      <div className="fw-semibold">{formatDateTime(importStatus?.lastImportDate)}</div>
                    </div>
                    <div>
                      <div className="text-secondary small mb-1">Season miles</div>
                      <div className="fw-semibold">{Number(importStatus?.athleteStats?.totalMiles ?? athlete?.seasonTotalMiles ?? 0).toFixed(1)} mi</div>
                    </div>
                    <div>
                      <div className="text-secondary small mb-1">Season points</div>
                      <div className="fw-semibold">{Number(importStatus?.athleteStats?.totalPoints ?? athlete?.seasonTotalPoints ?? 0).toFixed(1)}</div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="modern-card">
            <div className="modern-card-header">
              <h2 className="modern-card-title">Counted Activities</h2>
              <p className="modern-card-subtitle">Your 20 most recent activities and where each one came from. You can delete any activity in your account.</p>
            </div>
            <Card.Body>
              {syncedActivities.length === 0 ? (
                <p className="mb-0 text-secondary">No activities are available yet. Connect a service and import your latest workouts to get started.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Name</th>
                        <th>Miles</th>
                        <th>Service</th>
                        <th>Device</th>
                        <th>Base points</th>
                        <th>Variation</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncedActivities.map((event) => (
                        <tr key={event._id || `${event.startTime}-${event.distance}`}>
                          <td>{formatDateTime(event.startTime)}</td>
                          <td>{event.title || event.name || 'Workout'}</td>
                          <td>{formatMiles(event.distance)} mi</td>
                          <td>{getActivityService(event)}</td>
                          <td>{getActivityDevice(event)}</td>
                          <td>{Number(event.points || 0).toFixed(1)}</td>
                          <td>
                            {(() => {
                              const classification = event.scoringClassifications?.find((item) => item.league?.toString() === activeLeagueId?.toString());
                              if (classification) return <Badge bg={classification.status === 'approved' ? 'success' : classification.status === 'rejected' ? 'danger' : 'warning'} text={classification.status === 'pending' ? 'dark' : undefined}>{classification.variation} · {classification.status}</Badge>;
                              if (/trail/i.test(event.activityType || '')) return <Badge bg="info" text="dark">trail · provider</Badge>;
                              return <span className="text-secondary">standard</span>;
                            })()}
                          </td>
                          <td className="text-end">
                            <div className="d-inline-flex gap-2">
                              <Button size="sm" variant="outline-primary" disabled={!activeLeagueId} onClick={() => openDeclaration(event)}>Declare</Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={deletingEventId === event._id}
                                onClick={() => handleDeleteActivity(event)}
                              >
                                {deletingEventId === event._id ? 'Deleting…' : 'Delete'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      <Modal show={Boolean(declaration)} onHide={() => setDeclaration(null)} centered>
        <Form onSubmit={submitDeclaration}>
          <Modal.Header closeButton><Modal.Title>Activity Variation</Modal.Title></Modal.Header>
          <Modal.Body className="d-grid gap-3">
            <div className="text-secondary">{declaration?.eventName}</div>
            <Form.Group>
              <Form.Label>Variation</Form.Label>
              <Form.Select value={declaration?.variation || 'standard'} onChange={(event) => setDeclaration((current) => ({ ...current, variation: event.target.value }))}>
                <option value="standard">Standard</option>
                <option value="trail">Trail run</option>
                <option value="ruck">Ruck</option>
                <option value="stroller">Stroller</option>
              </Form.Select>
            </Form.Group>
            {declaration?.variation === 'ruck' && (
              <Form.Group>
                <Form.Label>Pack weight (lb)</Form.Label>
                <Form.Control type="number" min="0" max="200" required value={declaration.ruckWeightLbs} onChange={(event) => setDeclaration((current) => ({ ...current, ruckWeightLbs: event.target.value }))} />
              </Form.Group>
            )}
            {declaration?.variation !== 'standard' && <Alert variant="info" className="mb-0">Your league manager must approve this variation before its multiplier applies.</Alert>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setDeclaration(null)}>Cancel</Button>
            <Button type="submit" disabled={isDeclaring}>{isDeclaring ? 'Submitting...' : 'Submit'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ActivitiesScreen;
